/*
  Wishlist — shared store + header drawer.

  The heart buttons on product cards (snippets/card-product.liquid) write into
  localStorage under `mt-catalog-favorites`. This file owns that key so the
  header count, the drawer and every heart on the page stay in step; other
  scripts should go through `window.MTWishlist` rather than touching storage.

  Storage format is an array of objects:
    { id, handle, title, url, image, price, variantId, variantCount }

  The original build stored bare product ids as strings, so `read()` upgrades
  those in place. A legacy entry has no title or image, and is rendered in the
  drawer as a bare link until the shopper next sees that card.
*/
(function () {
  var KEY = 'mt-catalog-favorites';
  var CHANGE_EVENT = 'mt:wishlist:change';

  function read() {
    var raw;
    try {
      raw = JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (error) {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    return raw
      .map(function (entry) {
        if (entry && typeof entry === 'object') return entry;
        return { id: String(entry) };
      })
      .filter(function (entry) {
        return entry.id;
      });
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      // Storage can be unavailable (private mode, quota). The wishlist still
      // works for the current page — it just will not survive a reload.
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: items } }));
  }

  function has(id) {
    var wanted = String(id);
    return read().some(function (entry) {
      return String(entry.id) === wanted;
    });
  }

  function remove(id) {
    var wanted = String(id);
    write(
      read().filter(function (entry) {
        return String(entry.id) !== wanted;
      })
    );
  }

  function add(item) {
    if (!item || !item.id) return;
    var items = read().filter(function (entry) {
      return String(entry.id) !== String(item.id);
    });
    items.unshift(item);
    write(items);
  }

  /* Rewrites a saved entry in place, keeping its spot in the list. Does
     nothing when the item is not saved, and skips the write (and the change
     event) when nothing differs, so a caller can run it on every change of
     its own state. Returns whether the item is saved. */
  function update(item) {
    if (!item || !item.id) return false;
    var items = read();
    var index = -1;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].id) === String(item.id)) {
        index = i;
        break;
      }
    }
    if (index === -1) return false;
    if (JSON.stringify(items[index]) === JSON.stringify(item)) return true;
    items[index] = item;
    write(items);
    return true;
  }

  /* Returns the state the product is in *after* the toggle. */
  function toggle(item) {
    if (!item || !item.id) return false;
    if (has(item.id)) {
      remove(item.id);
      return false;
    }
    add(item);
    return true;
  }

  function count() {
    return read().length;
  }

  /* Reads a heart button's data-* attributes into a stored entry. */
  function itemFromButton(button) {
    var data = button.dataset;
    return {
      id: data.favoriteProduct,
      handle: data.favoriteHandle || '',
      title: data.favoriteTitle || '',
      url: data.favoriteUrl || '',
      image: data.favoriteImage || '',
      price: data.favoritePrice || '',
      variantId: data.favoriteVariant || '',
      variantCount: Number(data.favoriteVariantCount || 0),
      /* Optional. A saved custom kit (section-byo.js) carries a one-line
         summary of its parts and its own label for the drawer's link. */
      note: data.favoriteNote || '',
      actionLabel: data.favoriteAction || ''
    };
  }

  function syncButtons(root) {
    var items = read();
    (root || document).querySelectorAll('[data-favorite-product]').forEach(function (button) {
      var isFavorite = items.some(function (entry) {
        return String(entry.id) === String(button.dataset.favoriteProduct);
      });
      button.classList.toggle('is-favorite', isFavorite);
      button.setAttribute('aria-pressed', String(isFavorite));
    });
  }

  /* Heart flight, part one: the hold on the header count.

     When a product is saved, a heart lifts off the card's button and arcs up
     to the header heart (see flyHeart below). The store is written the moment
     the button is clicked — nothing about an animation is allowed to sit
     between the shopper and their data, and a flight interrupted by a
     navigation must not lose the save. What IS held back is the header count,
     because a number that ticks up before the heart arrives leaves the flight
     with nothing to deliver.

     A counter rather than a flag, so that with two quick saves the first
     landing does not release the second one's count. */
  var countHold = 0;

  function holdCount() {
    countHold += 1;
  }

  function releaseCount() {
    if (countHold > 0) countHold -= 1;
    syncCount();
  }

  function syncCount() {
    if (countHold > 0) return;
    var total = count();
    document.querySelectorAll('[data-wishlist-icon]').forEach(function (icon) {
      icon.classList.toggle('has-items', total > 0);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (bubble) {
      bubble.hidden = total === 0;
      var visible = bubble.querySelector('[data-wishlist-count-number]');
      if (visible) visible.textContent = total < 100 ? String(total) : '99+';
      var spoken = bubble.querySelector('[data-wishlist-count-text]');
      if (spoken) spoken.textContent = total === 1 ? '1 saved item' : total + ' saved items';
    });
  }

  window.MTWishlist = {
    key: KEY,
    changeEvent: CHANGE_EVENT,
    read: read,
    has: has,
    add: add,
    update: update,
    remove: remove,
    write: write,
    toggle: toggle,
    count: count,
    itemFromButton: itemFromButton,
    syncButtons: syncButtons,
    syncCount: syncCount
  };

  /* ── Heart flight ────────────────────────────────── */
  var HEART_PATH =
    'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z';
  var FLIGHT_MS = 720;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function centreOf(element) {
    var rect = element.getBoundingClientRect();
    if (!rect.width && !rect.height) return null; // display:none, or off-layout
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  /* Samples a quadratic Bezier into keyframes, because the two endpoints are
     only known at click time and cannot be written as CSS.

     The control point is lifted above BOTH endpoints, and that is what turns a
     straight diagonal into a lob: the heart rises out of the card first and
     comes down onto the header, rather than sliding across the page. The lift
     scales with the distance, so a card just under the header gets a small hop
     and one at the foot of a long page gets a real arc, with a floor under it
     so the hop never flattens out entirely. */
  function arcFrames(from, to) {
    var dx = to.x - from.x;
    var dy = to.y - from.y;
    var distance = Math.sqrt(dx * dx + dy * dy);
    var lift = Math.max(70, distance * 0.3);

    var control = {
      x: from.x + dx * 0.35,
      y: Math.min(from.y, to.y) - lift
    };

    var frames = [];
    var steps = 24;

    for (var i = 0; i <= steps; i += 1) {
      var t = i / steps;
      var inverse = 1 - t;

      var x = inverse * inverse * from.x + 2 * inverse * t * control.x + t * t * to.x;
      var y = inverse * inverse * from.y + 2 * inverse * t * control.y + t * t * to.y;

      /* Swells for the first sixth of the flight, then shrinks the rest of the
         way in. It reads as the heart receding toward the header, rather than
         simply getting smaller on the spot. */
      var scale = t < 0.16 ? 1 + (t / 0.16) * 0.25 : 1.25 - ((t - 0.16) / 0.84) * 0.9;

      frames.push({
        offset: t,
        transform:
          'translate(-50%, -50%) translate(' +
          (x - from.x) +
          'px, ' +
          (y - from.y) +
          'px) scale(' +
          scale +
          ')',
        opacity: t < 0.86 ? 1 : 1 - (t - 0.86) / 0.14
      });
    }

    return frames;
  }

  function popTarget(target) {
    if (!target) return;
    target.classList.remove('is-receiving');
    /* Reflow, so a second save mid-animation restarts the pop instead of being
       swallowed by the class already being on the element. */
    void target.offsetWidth;
    target.classList.add('is-receiving');
    setTimeout(function () {
      target.classList.remove('is-receiving');
    }, 500);
  }

  function flyHeart(button) {
    var target = document.querySelector('[data-wishlist-icon]');
    var from = centreOf(button);
    var to = target ? centreOf(target.querySelector('svg') || target) : null;

    /* No header heart on this layout, nothing measurable to fly between, or no
       Web Animations: release the count straight away so the badge is never
       left behind waiting for a landing that cannot happen. */
    if (!from || !to || typeof button.animate !== 'function') {
      releaseCount();
      popTarget(target);
      return;
    }

    var heart = document.createElement('span');
    heart.className = 'mt-wish-fly';
    heart.setAttribute('aria-hidden', 'true');
    heart.style.left = from.x + 'px';
    heart.style.top = from.y + 'px';
    heart.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="' + HEART_PATH + '"/></svg>';
    document.body.appendChild(heart);

    var animation = heart.animate(arcFrames(from, to), {
      duration: FLIGHT_MS,
      easing: 'cubic-bezier(0.4, 0, 0.25, 1)',
      fill: 'forwards'
    });

    /* One landing, whichever gets there first. `finish` is the normal path; the
       timeout covers a browser that throttles or drops the animation — a
       backgrounded tab, mostly — where waiting on the event alone would hold
       the count back for good. */
    var landed = false;

    function land() {
      if (landed) return;
      landed = true;
      if (heart.parentNode) heart.parentNode.removeChild(heart);
      releaseCount();
      popTarget(target);
    }

    animation.addEventListener('finish', land);
    setTimeout(land, FLIGHT_MS + 250);
  }

  /* Throws a ring of short-lived dots out of the card's heart as it is saved —
     the same burst as the Giving Back page's partnership heart
     (section-promise-page.js). The dots live on <body> with position: fixed
     rather than inside the button, because card media clips its overflow and
     would cut the ring off at the card edge. */
  var SPARK_COUNT = 12;

  function burstSparks(button) {
    var origin = centreOf(button.querySelector('svg') || button);
    if (!origin) return;

    for (var i = 0; i < SPARK_COUNT; i++) {
      var spark = document.createElement('span');
      spark.className = 'mt-wish-spark';
      spark.setAttribute('aria-hidden', 'true');

      var angle = (Math.PI * 2 * i) / SPARK_COUNT + (Math.random() - 0.5) * 0.5;
      var distance = 22 + Math.random() * 30;
      var size = 6 * (0.5 + Math.random() * 0.8);
      var life = 600 + Math.random() * 400;

      spark.style.left = origin.x + 'px';
      spark.style.top = origin.y + 'px';
      spark.style.width = size + 'px';
      spark.style.height = size + 'px';
      spark.style.setProperty('--dx', Math.cos(angle) * distance + 'px');
      spark.style.setProperty('--dy', Math.sin(angle) * distance + 'px');
      spark.style.setProperty('--dur', life + 'ms');

      document.body.appendChild(spark);
      setTimeout(
        (function (node) {
          return function () {
            if (node.parentNode) node.parentNode.removeChild(node);
          };
        })(spark),
        life + 200
      );
    }
  }

  /* ── Heart clicks ───────────────────────────────────
     Delegated from the document so cards rendered later — quick view,
     paginated grids, section reloads — are covered without rebinding. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-favorite-product]');
    if (!button) return;
    /* A disabled heart (the Build Your Own kit's, before any part is
       picked) saves nothing. Browsers already swallow most clicks on a
       disabled button; this covers the ones that still bubble. */
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    var reduced = prefersReducedMotion();
    if (!reduced) holdCount();

    var saved = toggle(itemFromButton(button));

    if (reduced) return;

    /* Only a save flies. Removing is not a delivery, and a heart flying INTO
       the header while the count goes down would say the opposite of what just
       happened. */
    if (!saved) {
      releaseCount();
      return;
    }

    button.classList.remove('is-launching');
    void button.offsetWidth;
    button.classList.add('is-launching');
    setTimeout(function () {
      button.classList.remove('is-launching');
    }, 850);

    burstSparks(button);
    flyHeart(button);
  });

  document.addEventListener(CHANGE_EVENT, function () {
    syncButtons(document);
    syncCount();
  });

  /* Another tab changed the wishlist. */
  window.addEventListener('storage', function (event) {
    if (event.key && event.key !== KEY) return;
    syncButtons(document);
    syncCount();
    var drawer = document.querySelector('wishlist-drawer');
    if (drawer && drawer.render) drawer.render();
  });

  /* ── Drawer ──────────────────────────────────────────────── */
  var FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23eef1f6"/></svg>'
    );

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-wishlist-overlay]');
      this.list = this.querySelector('[data-wishlist-list]');
      this.previousBodyOverflow = '';
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.initDrag();
      document.addEventListener(CHANGE_EVENT, this.render.bind(this));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('active')) this.close();
      });
      this.render();
    }

    onClick(event) {
      /* `event.target === this.overlay` used to close it too. It cannot now:
         the overlay no longer takes pointer events, and a panel the shopper
         is meant to keep working alongside must not vanish the moment they
         click the page behind it. The close button and Escape remain. */
      if (event.target.closest('[data-wishlist-close]')) {
        this.close();
        return;
      }

      var removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        remove(removeButton.dataset.wishlistRemove);
        return;
      }

      var addButton = event.target.closest('[data-wishlist-add]');
      if (addButton) this.addToCart(addButton);
    }

    open() {
      this.render();

      /* Always opens where it started. close() clears the drag offset once the
         panel is out of sight, and this is the belt to that braces: if the
         element was re-rendered, or a close was interrupted, opening still
         puts the card back under the header heart rather than wherever it was
         last dropped. */
      this.resetPosition();

      this.classList.add('active');

      /* `is-unfolding` is what lets the contents settle in behind the sheet as
         it flattens (see component-wishlist-drawer.css). It is a separate
         class from `active` on purpose: render() replaces the list's
         innerHTML on every change, so a stagger keyed off `active` would
         replay in full every time a single item was removed with the drawer
         already open. This class is only on for the length of the unfold. */
      this.classList.remove('is-unfolding');
      void this.offsetWidth;
      this.classList.add('is-unfolding');
      clearTimeout(this.unfoldTimer);
      this.unfoldTimer = setTimeout(
        function () {
          this.classList.remove('is-unfolding');
        }.bind(this),
        1400
      );

      /* No body scroll lock. The panel is non-modal now — it can be dragged
         out of the way and the rest of the site stays usable while it is
         open — so freezing the page behind it would be the one thing that
         stops the shopper carrying on. */
      var panel = this.querySelector('[data-wishlist-panel]');
      if (panel) panel.focus({ preventScroll: true });
      this.clampIntoView();
    }

    close() {
      this.classList.remove('active');
      this.classList.remove('is-unfolding');
      clearTimeout(this.unfoldTimer);

      /* Back to its starting position — but only once it has finished
         animating out. Clearing the offset now would snap the card across the
         screen to the corner in full view, which is the opposite of tidy. The
         delay covers the close transition in
         component-wishlist-drawer.css (~280ms) with a little margin. */
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(
        function () {
          this.resetPosition();
        }.bind(this),
        360
      );
      var trigger = document.querySelector('[data-wishlist-icon]');
      if (trigger) trigger.focus();
    }

    /* ── Drag ────────────────────────────────────────────────────
       The panel can be picked up by its header and put anywhere on screen.

       Offsets are applied with the individual `translate` property, not with
       `transform`. That matters: the open animation already owns `transform`
       and `scale` (see component-wishlist-drawer.css), and the three compose
       into one matrix instead of overwriting each other. Writing a translate
       into `transform` would have wiped the stretch mid-flight.

       Pointer events rather than mouse events, so a touch drag works from the
       same code. `setPointerCapture` keeps the drag alive when the pointer
       runs off the header — or off the window — which a plain move listener
       on the element would not. */
    initDrag() {
      this.dragX = 0;
      this.dragY = 0;

      var panel = this.querySelector('[data-wishlist-panel]');
      var handle = this.querySelector('[data-wishlist-drag]');
      if (!panel || !handle) return;

      var self = this;
      var startX = 0;
      var startY = 0;
      var originX = 0;
      var originY = 0;
      var pointerId = null;

      handle.addEventListener('pointerdown', function (event) {
        // The close button lives in the header; a press on it is not a drag.
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        if (event.target.closest('[data-wishlist-close]')) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        originX = self.dragX;
        originY = self.dragY;

        self.classList.add('is-dragging');
        handle.setPointerCapture(pointerId);
        event.preventDefault();
      });

      handle.addEventListener('pointermove', function (event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        self.moveTo(originX + (event.clientX - startX), originY + (event.clientY - startY));
      });

      function end(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
        pointerId = null;
        self.classList.remove('is-dragging');
      }

      handle.addEventListener('pointerup', end);
      handle.addEventListener('pointercancel', end);

      /* A window narrowed while the panel sits open would otherwise leave it
         stranded off-screen with no way back. */
      window.addEventListener('resize', function () {
        if (self.classList.contains('active')) self.clampIntoView();
      });
    }

    /* Applies an offset, held to the viewport. The panel is placed by margins
       in a flex host, so its untranslated position has to be derived by
       subtracting the offset already applied — measuring alone would give the
       position it has been dragged to, and the clamp would creep. */
    moveTo(x, y) {
      var panel = this.querySelector('[data-wishlist-panel]');
      if (!panel) return;

      var rect = panel.getBoundingClientRect();
      var baseLeft = rect.left - this.dragX;
      var baseTop = rect.top - this.dragY;

      // Keep a strip of the panel, and all of its header, reachable.
      var EDGE = 120;
      var BAR = 60;

      var minX = EDGE - baseLeft - rect.width;
      var maxX = window.innerWidth - EDGE - baseLeft;
      var minY = -baseTop;
      var maxY = window.innerHeight - BAR - baseTop;

      this.dragX = Math.min(Math.max(x, minX), maxX);
      this.dragY = Math.min(Math.max(y, minY), maxY);

      panel.style.translate = this.dragX + 'px ' + this.dragY + 'px';
    }

    resetPosition() {
      this.dragX = 0;
      this.dragY = 0;
      var panel = this.querySelector('[data-wishlist-panel]');
      if (panel) panel.style.translate = '';
    }

    clampIntoView() {
      if (!this.dragX && !this.dragY) return;
      this.moveTo(this.dragX, this.dragY);
    }

    render() {
      if (!this.list) return;
      var items = read();

      this.classList.toggle('is-empty', items.length === 0);
      var heading = this.querySelector('[data-wishlist-heading-count]');
      if (heading) heading.textContent = items.length === 1 ? '1 item' : items.length + ' items';

      this.list.innerHTML = items.map(renderItem).join('');
    }

    async addToCart(button) {
      /* Products with options cannot be resolved to a single variant here, so
         the card sends the shopper to the product page instead. */
      var variantId = button.dataset.wishlistAdd;
      if (!variantId) return;

      var original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Unable to add product');

        var cart = await fetch(root + 'cart.js').then(function (cartResponse) {
          return cartResponse.json();
        });
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (node) {
          node.textContent = cart.item_count;
        });

        button.textContent = 'Added';
        window.setTimeout(function () {
          button.disabled = false;
          button.textContent = original;
        }, 1200);
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Try again';
      }
    }
  }

  function renderItem(item) {
    var url = item.url || (item.handle ? '/products/' + item.handle : '');
    var title = item.title || 'View product';
    var canAddDirectly = item.variantId && Number(item.variantCount || 1) === 1;

    return (
      '<li class="mt-wl-item">' +
      (url ? '<a class="mt-wl-item__media" href="' + escapeHtml(url) + '" tabindex="-1">' : '<span class="mt-wl-item__media">') +
      '<img src="' + escapeHtml(item.image || FALLBACK_IMAGE) + '" alt="" width="72" height="86" loading="lazy">' +
      (url ? '</a>' : '</span>') +
      '<div class="mt-wl-item__body">' +
      (url
        ? '<a class="mt-wl-item__title" href="' + escapeHtml(url) + '">' + escapeHtml(title) + '</a>'
        : '<span class="mt-wl-item__title">' + escapeHtml(title) + '</span>') +
      (item.note ? '<span class="mt-wl-item__note">' + escapeHtml(item.note) + '</span>' : '') +
      (item.price ? '<span class="mt-wl-item__price">' + escapeHtml(item.price) + '</span>' : '') +
      '<div class="mt-wl-item__actions">' +
      (canAddDirectly
        ? '<button type="button" class="mt-wl-item__add" data-wishlist-add="' + escapeHtml(item.variantId) + '">Add to cart</button>'
        : url
        ? '<a class="mt-wl-item__add" href="' + escapeHtml(url) + '">' + escapeHtml(item.actionLabel || 'Choose options') + '</a>'
        : '') +
      '</div>' +
      '</div>' +
      '<button type="button" class="mt-wl-item__remove" data-wishlist-remove="' +
      escapeHtml(item.id) +
      '" aria-label="Remove ' +
      escapeHtml(title) +
      ' from saved items">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '</li>'
    );
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  /* ── Header trigger ──────────────────────────────────────── */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-wishlist-icon]');
    if (!trigger) return;

    var drawer = document.querySelector('wishlist-drawer');
    if (!drawer) return;

    event.preventDefault();
    drawer.open();
  });

  function initialize() {
    syncButtons(document);
    syncCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
